import React, { useState, useEffect, useRef } from 'react';
import ReactModal from 'react-modal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, del } from '@utils/axios_api';
import { toast } from 'react-toastify';

const fetchCardDetail   = (cardId) => get(`/bizscanpro/cards/${cardId}`);
const fetchCardGroupIds = (cardId) => get(`/biz/cards/${cardId}/group-ids`);
const fetchShareLink    = (cardId) => get(`/biz/cards/${cardId}/share`, { suppressToast: true });

const STATUS_BADGE = {
    pending_review: { bg: '#fef9c3', color: '#92400e', label: '검수 대기' },
    completed:      { bg: '#dcfce7', color: '#166534', label: '확정 완료' },
    ocr_failed:     { bg: '#fee2e2', color: '#991b1b', label: 'OCR 오류' },
    uploaded:       { bg: '#f3f4f6', color: '#4b5563', label: '업로드됨' },
    ocr_completed:  { bg: '#dbeafe', color: '#1d4ed8', label: 'OCR 완료' },
};

const CONTACT_FIELDS = [
    { key: 'phone',   label: '전화' },
    { key: 'email',   label: '이메일' },
    { key: 'address', label: '주소' },
    { key: 'website', label: '웹사이트' },
];

function parseJsonField(raw) {
    if (!raw) return null;
    if (typeof raw === 'string') { try { return JSON.parse(raw); } catch { return null; } }
    return raw;
}

function buildFieldMap(card) {
    const finalJson = parseJsonField(card?.admin_final_json);
    const llmJson   = parseJsonField(card?.llm_structured_json);
    const apiFields = finalJson?.fields ?? llmJson?.fields ?? [];
    const map = {};
    for (const f of apiFields) {
        if (f?.key && f?.value) map[f.key] = f.value;
    }
    return map;
}

function escapeVCardValue(value) {
    return String(value).replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
}

function buildVCard(fields) {
    const lines = ['BEGIN:VCARD', 'VERSION:3.0'];
    if (fields.person_name)  lines.push(`FN:${escapeVCardValue(fields.person_name)}`);
    if (fields.company_name) lines.push(`ORG:${escapeVCardValue(fields.company_name)}`);
    if (fields.job_title)    lines.push(`TITLE:${escapeVCardValue(fields.job_title)}`);
    if (fields.phone)        lines.push(`TEL;TYPE=WORK,VOICE:${escapeVCardValue(fields.phone)}`);
    if (fields.email)        lines.push(`EMAIL:${escapeVCardValue(fields.email)}`);
    if (fields.address)      lines.push(`ADR;TYPE=WORK:;;${escapeVCardValue(fields.address)};;;;`);
    if (fields.website)      lines.push(`URL:${escapeVCardValue(fields.website)}`);
    lines.push('END:VCARD');
    return lines.join('\r\n');
}

function downloadVCard(fields) {
    const vcard = buildVCard(fields);
    const blob = new Blob([vcard], { type: 'text/vcard;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${fields.person_name || '명함'}.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}

function contactHref(key, value) {
    if (key === 'phone')   return `tel:${value.replace(/[^0-9+]/g, '')}`;
    if (key === 'email')   return `mailto:${value}`;
    if (key === 'website') return /^https?:\/\//i.test(value) ? value : `https://${value}`;
    return null;
}

export default function BizCardDetailModal({ cardId, groups = [], onClose, onReview, onGroupsChanged }) {
    const queryClient = useQueryClient();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [shareLoading, setShareLoading] = useState(false);
    const mobileScrollRef = useRef(null);
    const shareLinkRef    = useRef(null);
    const [groupLoading, setGroupLoading] = useState(false);
    const [imgZoom,      setImgZoom]      = useState(1);
    const [detailTab,    setDetailTab]    = useState('llm');

    const { data, isLoading } = useQuery({
        queryKey:  ['bizCardDetail', cardId],
        queryFn:   () => fetchCardDetail(cardId),
        enabled:   !!cardId,
        staleTime: 30_000,
    });

    const { data: groupIdsData, refetch: refetchGroupIds } = useQuery({
        queryKey:  ['bizCardGroupIds', cardId],
        queryFn:   () => fetchCardGroupIds(cardId),
        enabled:   !!cardId,
        staleTime: 10_000,
    });

    const { data: shareLinkData, isLoading: shareLinkQueryLoading } = useQuery({
        queryKey:  ['bizCardShareLink', cardId],
        queryFn:   () => fetchShareLink(cardId),
        enabled:   !!cardId,
        staleTime: 0,
        retry: false,
    });

    const resolvedShareLink = shareLinkData?.result?.token ?? null;

    const cardGroupIds = new Set(Array.isArray(groupIdsData?.result) ? groupIdsData.result : []);
    const assignedGroups  = groups.filter((g) => cardGroupIds.has(g.id));
    const availableGroups = groups.filter((g) => !cardGroupIds.has(g.id));

    const handleGenerateShareLink = async () => {
        setShareLoading(true);
        try {
            await post(`/biz/cards/${cardId}/share`);
            await queryClient.invalidateQueries({ queryKey: ['bizCardShareLink', cardId] });
            toast.success('공유 링크가 생성되었습니다.');
            if (isMobile) {
                setTimeout(() => shareLinkRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 80);
            }
        } catch {
            toast.error('공유 링크 생성에 실패했습니다.');
        } finally {
            setShareLoading(false);
        }
    };

    const handleRevokeShareLink = async () => {
        if (!window.confirm('공유 링크를 삭제하시겠습니까?')) return;
        setShareLoading(true);
        try {
            await del(`/biz/cards/${cardId}/share`);
            await queryClient.invalidateQueries({ queryKey: ['bizCardShareLink', cardId] });
            toast.success('공유 링크가 삭제되었습니다.');
        } catch {
            toast.error('공유 링크 삭제에 실패했습니다.');
        } finally {
            setShareLoading(false);
        }
    };

    const handleToggleGroup = async (groupId, inGroup) => {
        setGroupLoading(true);
        try {
            if (inGroup) {
                await del(`/biz/groups/${groupId}/cards/${cardId}`);
            } else {
                await post(`/biz/groups/${groupId}/cards/${cardId}`);
            }
            refetchGroupIds();
            queryClient.invalidateQueries({ queryKey: ['bizGroupCards', groupId] });
            if (onGroupsChanged) onGroupsChanged();
        } catch {
            toast.error(inGroup ? '그룹에서 제거하지 못했습니다.' : '그룹에 추가하지 못했습니다.');
        } finally {
            setGroupLoading(false);
        }
    };

    useEffect(() => {
        const fn = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', fn);
        return () => window.removeEventListener('resize', fn);
    }, []);

    const shareUrl = resolvedShareLink
        ? `${window.location.origin}/share/${resolvedShareLink}`
        : null;

    const imageUrl = data?.image_url
        ? `${process.env.REACT_APP_BOOT_URL}/api${data.image_url}`
        : null;

    const fields  = buildFieldMap(data);
    const status  = data?.status ?? null;
    const badge   = STATUS_BADGE[status] ?? null;
    const isOwner = data?.is_owner === true;

    return (
        <ReactModal
            isOpen={!!cardId}
            onRequestClose={onClose}
            overlayClassName="modal-overlay"
            className="modal-wrap"
        >
            <div
                className="modal-container bcdm-container"
                style={{ maxWidth: '820px', width: isMobile ? '100%' : undefined, minWidth: isMobile ? 0 : '640px', padding: 0, overflow: 'hidden', borderRadius: isMobile ? 0 : '10px', boxShadow: '0 18px 44px rgba(15,23,42,0.18)', ...(isMobile ? { display: 'flex', flexDirection: 'column', height: '100dvh', maxHeight: '100dvh' } : {}) }}
            >
                <style>{`
                    .bcdm-close { transition: background 0.15s, color 0.15s; }
                    .bcdm-close:hover { background: #f1f5f9; color: #475569; }
                    .bcdm-chip { transition: border-color 0.12s, background 0.12s, color 0.12s, transform 0.12s; }
                    .bcdm-chip:hover:not(:disabled) { border-color: #94a3b8; transform: translateY(-1px); }
                    .bcdm-chip.is-active:hover:not(:disabled) { border-color: #2563eb; }
                    .bcdm-link-btn { transition: background 0.12s, border-color 0.12s; }
                    .bcdm-link-btn:hover:not(:disabled) { background: #f8fafc; }
                    .bcdm-link-btn.is-danger:hover:not(:disabled) { background: #fef2f2; border-color: #f87171; }
                    .bcdm-link-btn.is-primary:hover:not(:disabled) { background: #dbeafe; }
                    .bcdm-input:focus { border-color: #93c5fd !important; box-shadow: 0 0 0 3px rgba(59,130,246,0.12); }
                    .bcdm-row:hover .bcdm-row-value { color: #1e293b; }
                    .bcdm-contact-link:hover { color: #1d4ed8 !important; text-decoration: underline; }
                `}</style>

                {/* ── 헤더 ── */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 22px',
                    borderBottom: '1px solid #eef0f2',
                    background: '#fff',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', letterSpacing: '-0.2px' }}>명함 상세</span>
                        {isOwner && badge && (
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: '5px',
                                fontSize: '11px', fontWeight: '600', padding: '3px 10px 3px 8px',
                                borderRadius: '999px', background: badge.bg, color: badge.color,
                            }}>
                                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: badge.color, display: 'inline-block' }} />
                                {badge.label}
                            </span>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        className="bcdm-close"
                        style={{ background: 'none', border: 'none', borderRadius: '6px', fontSize: '18px', color: '#9ca3af', cursor: 'pointer', lineHeight: 1, padding: '4px 8px' }}
                    >×</button>
                </div>

                {/* ── 본문 ── */}
                {isLoading ? (
                    <div style={{ height: '260px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '14px' }}>
                        로딩 중...
                    </div>
                ) : isMobile ? (
                        /* ══ 모바일 레이아웃 ══ */
                        <div ref={mobileScrollRef} style={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>

                            {/* 이미지 배너 */}
                            <div style={{ height: '160px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                {imageUrl
                                    ? <img src={imageUrl} alt="명함" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                    : <span style={{ fontSize: '12px', color: '#94a3b8' }}>이미지 없음</span>
                                }
                            </div>

                            {/* 이름 / 직함 / 회사 */}
                            <div style={{ padding: '16px 16px 12px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '4px' }}>
                                    <p style={{ margin: 0, fontSize: '20px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.3px', lineHeight: 1.2 }}>
                                        {fields.person_name || <span style={{ color: '#cbd5e1' }}>이름 없음</span>}
                                    </p>
                                    {isOwner && badge && (
                                        <span style={{ flexShrink: 0, fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '999px', background: badge.bg, color: badge.color }}>
                                            {badge.label}
                                        </span>
                                    )}
                                </div>
                                {(fields.job_title || fields.company_name) && (
                                    <p style={{ margin: 0, fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
                                        {[fields.job_title, fields.company_name].filter(Boolean).join(' · ')}
                                    </p>
                                )}
                            </div>

                            {/* 연락처 필드 */}
                            <div style={{ borderTop: '1px solid #f1f5f9', padding: '0 16px' }}>
                                {CONTACT_FIELDS.map(({ key, label }) => {
                                    const value = fields[key];
                                    if (!value) return null;
                                    const href = contactHref(key, value);
                                    return (
                                        <div key={key} style={{ display: 'flex', alignItems: 'center', minHeight: '48px', borderBottom: '1px solid #f8fafc', gap: '12px' }}>
                                            <span style={{ width: '48px', flexShrink: 0, fontSize: '11px', fontWeight: '700', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                {label}
                                            </span>
                                            {href ? (
                                                <a href={href} target={key === 'website' ? '_blank' : undefined} rel={key === 'website' ? 'noopener noreferrer' : undefined}
                                                   style={{ flex: 1, fontSize: '14px', color: '#2563eb', wordBreak: 'break-all', textDecoration: 'none' }}>
                                                    {value}
                                                </a>
                                            ) : (
                                                <span style={{ flex: 1, fontSize: '14px', color: '#374151', wordBreak: 'break-all' }}>{value}</span>
                                            )}
                                        </div>
                                    );
                                })}
                                {CONTACT_FIELDS.every(({ key }) => !fields[key]) && !fields.person_name && (
                                    <p style={{ padding: '16px 0', fontSize: '13px', color: '#9ca3af' }}>추출된 정보가 없습니다.</p>
                                )}
                            </div>

                            {/* vCard 버튼 */}
                            {(fields.phone || fields.email) && (
                                <div style={{ padding: '12px 16px 4px' }}>
                                    <button type="button" onClick={() => downloadVCard(fields)}
                                        style={{ width: '100%', padding: '11px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', fontSize: '13px', fontWeight: '600', color: '#334155', cursor: 'pointer' }}>
                                        연락처 저장 (vCard)
                                    </button>
                                </div>
                            )}

                            {/* 그룹 */}
                            {groups.length > 0 && (
                                <div style={{ margin: '12px 16px 0', padding: '12px', background: '#fafbfc', border: '1px solid #eef0f2', borderRadius: '8px' }}>
                                    <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>그룹</p>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                                        {assignedGroups.length === 0 && <span style={{ fontSize: '12px', color: '#9ca3af' }}>소속된 그룹 없음</span>}
                                        {assignedGroups.map((g) => (
                                            <span key={g.id} className="bcdm-chip is-active"
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 6px 4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: '600', border: '1px solid #3b82f6', background: '#eff6ff', color: '#1d4ed8' }}>
                                                {g.groupName}
                                                <button type="button" disabled={groupLoading} onClick={() => handleToggleGroup(g.id, true)}
                                                    style={{ border: 'none', background: 'none', padding: '0 3px', fontSize: '13px', lineHeight: 1, cursor: groupLoading ? 'not-allowed' : 'pointer', color: '#60a5fa' }}>✕</button>
                                            </span>
                                        ))}
                                        {availableGroups.length > 0 && (
                                            <select disabled={groupLoading} value="" onChange={(e) => { if (e.target.value) handleToggleGroup(Number(e.target.value), false); }} className="bcdm-chip"
                                                style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '500', border: '1px dashed #cbd5e1', background: '#fff', color: '#6b7280', cursor: groupLoading ? 'not-allowed' : 'pointer' }}>
                                                <option value="">+ 그룹에 추가</option>
                                                {availableGroups.map((g) => <option key={g.id} value={g.id}>{g.groupName}</option>)}
                                            </select>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* 공유 링크 */}
                            {isOwner && (
                                <div ref={shareLinkRef} style={{ margin: '12px 16px 16px', padding: '12px', background: '#fafbfc', border: '1px solid #eef0f2', borderRadius: '8px' }}>
                                    <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>공유 링크</p>
                                    {shareLinkQueryLoading ? (
                                        <span style={{ fontSize: '12px', color: '#9ca3af' }}>확인 중...</span>
                                    ) : shareUrl ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <button type="button" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success('링크 복사됨'); }}
                                                style={{ width: '100%', padding: '10px', background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: '6px', fontSize: '13px', fontWeight: '600', color: '#1d4ed8', cursor: 'pointer' }}>
                                                링크 복사
                                            </button>
                                            <button type="button" onClick={handleRevokeShareLink} disabled={shareLoading}
                                                style={{ width: '100%', padding: '9px', background: '#fff', border: '1px solid #fca5a5', borderRadius: '6px', fontSize: '12px', color: '#dc2626', cursor: shareLoading ? 'not-allowed' : 'pointer' }}>
                                                {shareLoading ? '처리 중...' : '링크 삭제'}
                                            </button>
                                        </div>
                                    ) : status === 'pending_review' ? (
                                        <div style={{ fontSize: '12px', color: '#92400e', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: '5px', padding: '8px 12px' }}>
                                            검수 완료 후 링크 생성 가능
                                        </div>
                                    ) : (
                                        <button type="button" onClick={handleGenerateShareLink} disabled={shareLoading}
                                            style={{ width: '100%', padding: '10px', background: '#eff6ff', border: '1px solid #3b82f6', borderRadius: '6px', fontSize: '13px', fontWeight: '600', color: '#1d4ed8', cursor: shareLoading ? 'not-allowed' : 'pointer' }}>
                                            {shareLoading ? '생성 중...' : '공유 링크 생성'}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        /* ══ 데스크탑 레이아웃 ══ */
                        <>
                        {/* 상단 2컬럼 */}
                        <div style={{ display: 'flex', flexDirection: 'row' }}>

                            {/* 왼쪽: 이미지 패널 */}
                            <div style={{
                                width: '300px',
                                flexShrink: 0,
                                background: 'linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%)',
                                borderRight: '1px solid #eef0f2',
                                padding: '28px 22px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '14px',
                            }}>
                                <div style={{ width: '100%' }}>
                                    <div style={{
                                        width: '100%', aspectRatio: '1.6 / 1', borderRadius: '8px', overflow: 'hidden',
                                        boxShadow: '0 8px 20px rgba(15,23,42,0.16)', background: '#e2e8f0',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: imgZoom > 1 ? 'zoom-out' : 'zoom-in',
                                    }}>
                                        {imageUrl ? (
                                            <img src={imageUrl} alt="명함" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transform: `scale(${imgZoom})`, transition: 'transform 0.2s ease', transformOrigin: 'center center' }} />
                                        ) : (
                                            <span style={{ fontSize: '11px', color: '#94a3b8' }}>이미지 없음</span>
                                        )}
                                    </div>
                                    {imageUrl && (
                                        <div style={{ display: 'flex', justifyContent: 'center', gap: '6px', marginTop: '8px' }}>
                                            <button type="button" onClick={() => setImgZoom((z) => Math.max(1, +(z - 0.5).toFixed(1)))}
                                                style={{ fontSize: '12px', padding: '2px 10px', border: '1px solid #cbd5e1', borderRadius: '4px', background: '#fff', cursor: 'pointer', color: '#475569' }}>−</button>
                                            <span style={{ fontSize: '11px', color: '#94a3b8', alignSelf: 'center' }}>{imgZoom.toFixed(1)}×</span>
                                            <button type="button" onClick={() => setImgZoom((z) => Math.min(3, +(z + 0.5).toFixed(1)))}
                                                style={{ fontSize: '12px', padding: '2px 10px', border: '1px solid #cbd5e1', borderRadius: '4px', background: '#fff', cursor: 'pointer', color: '#475569' }}>+</button>
                                        </div>
                                    )}
                                </div>

                                {(data?.created_at || data?.confirmed_by) && (
                                    <div style={{ width: '100%', fontSize: '11px', color: '#94a3b8', lineHeight: '1.8', textAlign: 'center' }}>
                                        {data.created_at   && <div>{data.created_at.slice(0, 10)} 등록</div>}
                                        {data.confirmed_by && <div>{data.confirmed_by} 확정</div>}
                                    </div>
                                )}

                                {data?.ocr_raw_json?.full_text && (
                                    <details style={{ width: '100%' }}>
                                        <summary style={{ fontSize: '11px', color: '#94a3b8', cursor: 'pointer', userSelect: 'none', textAlign: 'center' }}>OCR 원문 보기</summary>
                                        <pre style={{ marginTop: '8px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '4px', padding: '8px', fontSize: '10px', color: '#64748b', whiteSpace: 'pre-wrap', maxHeight: '100px', overflowY: 'auto' }}>
                                            {data.ocr_raw_json.full_text}
                                        </pre>
                                    </details>
                                )}
                            </div>

                            {/* 오른쪽: 명함 정보 */}
                            <div style={{ flex: 1, padding: '24px 24px 20px' }}>
                                <div style={{ marginBottom: '20px', paddingBottom: '16px', borderBottom: '1px solid #f1f5f9' }}>
                                    <p style={{ margin: 0, fontSize: '21px', fontWeight: '800', color: '#0f172a', letterSpacing: '-0.4px' }}>
                                        {fields.person_name || <span style={{ color: '#cbd5e1' }}>이름 없음</span>}
                                    </p>
                                    {(fields.job_title || fields.company_name) && (
                                        <p style={{ margin: '5px 0 0', fontSize: '13px', color: '#64748b', fontWeight: '500' }}>
                                            {[fields.job_title, fields.company_name].filter(Boolean).join(' · ')}
                                        </p>
                                    )}
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    {CONTACT_FIELDS.map(({ key, label }) => {
                                        const value = fields[key];
                                        if (!value) return null;
                                        const href = contactHref(key, value);
                                        return (
                                            <div key={key} className="bcdm-row" style={{ display: 'flex', alignItems: 'flex-start', padding: '5px 0' }}>
                                                <span style={{ width: '52px', flexShrink: 0, fontSize: '11px', fontWeight: '700', color: '#94a3b8', paddingTop: '1px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                                    {label}
                                                </span>
                                                {href ? (
                                                    <a href={href} target={key === 'website' ? '_blank' : undefined} rel={key === 'website' ? 'noopener noreferrer' : undefined}
                                                       className="bcdm-row-value bcdm-contact-link"
                                                       style={{ fontSize: '13px', color: '#2563eb', lineHeight: '1.5', wordBreak: 'break-all', transition: 'color 0.12s', textDecoration: 'none' }}>
                                                        {value}
                                                    </a>
                                                ) : (
                                                    <span className="bcdm-row-value" style={{ fontSize: '13px', color: '#374151', lineHeight: '1.5', wordBreak: 'break-all', transition: 'color 0.12s' }}>
                                                        {value}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                    {CONTACT_FIELDS.every(({ key }) => !fields[key]) && !fields.person_name && (
                                        <p style={{ fontSize: '13px', color: '#9ca3af' }}>추출된 정보가 없습니다.</p>
                                    )}
                                </div>

                                {(fields.phone || fields.email) && (
                                    <button type="button" onClick={() => downloadVCard(fields)} className="bcdm-link-btn"
                                        style={{ marginTop: '16px', fontSize: '12px', padding: '6px 14px', border: '1px solid #cbd5e1', borderRadius: '6px', background: '#fff', color: '#334155', cursor: 'pointer', fontWeight: '600' }}>
                                        연락처 저장 (vCard)
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* OCR / LLM 비교 탭 + LLM 디버그 */}
                        {isOwner && (data?.ocr_raw_json?.fields?.length > 0 || data?.llm_structured_json?.fields?.length > 0 || data?.llm_called != null) && (() => {
                            const ocrJson   = parseJsonField(data?.ocr_raw_json);
                            const llmJson2  = parseJsonField(data?.llm_structured_json);
                            const ocrFields = Array.isArray(ocrJson?.fields)  ? ocrJson.fields  : [];
                            const llmFields = Array.isArray(llmJson2?.fields) ? llmJson2.fields : [];
                            const tabFields = detailTab === 'ocr' ? ocrFields : llmFields;
                            const LLM_DEBUG = [
                                { label: 'LLM 요청 여부', value: data?.llm_enabled_for_request },
                                { label: 'LLM 실제 호출', value: data?.llm_called },
                                { label: 'LLM 요청 모드', value: data?.llm_request_mode ?? '-' },
                                { label: 'LLM 성공 여부', value: data?.llm_call_success },
                            ];
                            return (
                                <div style={{ borderTop: '1px solid #eef0f2', padding: '16px 22px' }}>
                                    <div style={{ display: 'flex', gap: '4px', marginBottom: '12px', borderBottom: '1px solid #e5e7eb' }}>
                                        {[{ key: 'llm', label: 'LLM 필드' }, { key: 'ocr', label: 'OCR 필드' }].map((t) => (
                                            <button key={t.key} type="button" onClick={() => setDetailTab(t.key)}
                                                style={{ padding: '6px 16px', fontSize: '12px', fontWeight: detailTab === t.key ? '600' : '400', color: detailTab === t.key ? '#1d4ed8' : '#6b7280', background: 'none', border: 'none', borderBottom: detailTab === t.key ? '2px solid #3b82f6' : '2px solid transparent', cursor: 'pointer' }}>
                                                {t.label}
                                            </button>
                                        ))}
                                    </div>
                                    {tabFields.length > 0 ? (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', marginBottom: '12px' }}>
                                            {tabFields.map((f, i) => (
                                                <div key={i} style={{ display: 'flex', gap: '6px', padding: '3px 0', borderBottom: '1px solid #f8fafc', alignItems: 'flex-start' }}>
                                                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', minWidth: '72px', paddingTop: '1px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{f.key || '-'}</span>
                                                    <span style={{ fontSize: '12px', color: '#334155', wordBreak: 'break-word', flex: 1 }}>
                                                        {f.value || <span style={{ color: '#c7d2dc' }}>—</span>}
                                                        {Array.isArray(f.alternatives) && f.alternatives.length > 0 && (
                                                            <span style={{ marginLeft: '6px', fontSize: '10px', color: '#94a3b8' }}>({f.alternatives.join(' / ')})</span>
                                                        )}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p style={{ fontSize: '12px', color: '#9ca3af', marginBottom: '12px' }}>필드 없음</p>
                                    )}
                                    {data?.llm_called != null && (
                                        <details style={{ marginTop: '4px' }}>
                                            <summary style={{ fontSize: '11px', color: '#94a3b8', cursor: 'pointer', userSelect: 'none' }}>LLM 디버그 정보</summary>
                                            <div style={{ display: 'flex', gap: '12px', marginTop: '8px', flexWrap: 'wrap' }}>
                                                {LLM_DEBUG.map(({ label, value }) => (
                                                    <div key={label} style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '5px', padding: '5px 10px', minWidth: '80px' }}>
                                                        <p style={{ margin: 0, fontSize: '10px', color: '#94a3b8', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</p>
                                                        <p style={{ margin: '2px 0 0', fontSize: '13px', fontWeight: '600', color: typeof value === 'boolean' ? (value ? '#166534' : '#9ca3af') : '#334155' }}>
                                                            {typeof value === 'boolean' ? (value ? '✓' : '✗') : (value ?? '-')}
                                                        </p>
                                                    </div>
                                                ))}
                                            </div>
                                        </details>
                                    )}
                                </div>
                            );
                        })()}

                        {/* 하단: 그룹 + 공유 링크 */}
                        <div style={{ borderTop: '1px solid #eef0f2', background: '#fafbfc', padding: '16px 20px', display: 'flex', gap: '28px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
                            {groups.length > 0 && (
                                <div style={{ flex: '1 1 160px' }}>
                                    <p style={{ margin: '0 0 9px', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>그룹</p>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                                        {assignedGroups.length === 0 && <span style={{ fontSize: '12px', color: '#9ca3af' }}>소속된 그룹 없음</span>}
                                        {assignedGroups.map((g) => (
                                            <span key={g.id} className="bcdm-chip is-active"
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 6px 4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: '600', border: '1px solid #3b82f6', background: '#eff6ff', color: '#1d4ed8' }}>
                                                {g.groupName}
                                                <button type="button" disabled={groupLoading} onClick={() => handleToggleGroup(g.id, true)} title="그룹에서 제거"
                                                    style={{ border: 'none', background: 'none', padding: '0 3px', fontSize: '13px', lineHeight: 1, cursor: groupLoading ? 'not-allowed' : 'pointer', color: '#60a5fa' }}>✕</button>
                                            </span>
                                        ))}
                                        {availableGroups.length > 0 && (
                                            <select disabled={groupLoading} value="" onChange={(e) => { if (e.target.value) handleToggleGroup(Number(e.target.value), false); }} className="bcdm-chip"
                                                style={{ padding: '4px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '500', border: '1px dashed #cbd5e1', background: '#fff', color: '#6b7280', cursor: groupLoading ? 'not-allowed' : 'pointer' }}>
                                                <option value="">+ 그룹에 추가</option>
                                                {availableGroups.map((g) => <option key={g.id} value={g.id}>{g.groupName}</option>)}
                                            </select>
                                        )}
                                    </div>
                                </div>
                            )}
                            {isOwner && (
                                <div style={{ flex: '1 1 220px' }}>
                                    <p style={{ margin: '0 0 9px', fontSize: '11px', fontWeight: '700', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>공유 링크</p>
                                    {shareLinkQueryLoading ? (
                                        <span style={{ fontSize: '12px', color: '#9ca3af' }}>확인 중...</span>
                                    ) : shareUrl ? (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                                                <input readOnly value={shareUrl} onFocus={(e) => e.target.select()} className="bcdm-input"
                                                    style={{ flex: 1, minWidth: 0, fontSize: '11px', padding: '5px 9px', border: '1px solid #e2e8f0', borderRadius: '5px', background: '#fff', color: '#475569', outline: 'none', transition: 'border-color 0.12s, box-shadow 0.12s' }} />
                                                <button type="button" onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success('링크 복사됨'); }} className="bcdm-link-btn"
                                                    style={{ flexShrink: 0, fontSize: '12px', padding: '5px 11px', border: '1px solid #cbd5e1', borderRadius: '5px', background: '#fff', color: '#334155', cursor: 'pointer' }}>복사</button>
                                            </div>
                                            <button type="button" onClick={handleRevokeShareLink} disabled={shareLoading} className="bcdm-link-btn is-danger"
                                                style={{ alignSelf: 'flex-start', fontSize: '12px', padding: '5px 11px', border: '1px solid #fca5a5', borderRadius: '5px', background: '#fff', color: '#dc2626', cursor: shareLoading ? 'not-allowed' : 'pointer' }}>링크 삭제</button>
                                        </div>
                                    ) : status === 'pending_review' ? (
                                        <div style={{ fontSize: '12px', color: '#92400e', background: '#fef9c3', border: '1px solid #fde68a', borderRadius: '5px', padding: '5px 12px' }}>검수 완료 후 링크 생성 가능</div>
                                    ) : (
                                        <button type="button" onClick={handleGenerateShareLink} disabled={shareLoading} className="bcdm-link-btn is-primary"
                                            style={{ fontSize: '12px', padding: '5px 14px', border: '1px solid #3b82f6', borderRadius: '5px', background: '#eff6ff', color: '#1d4ed8', cursor: shareLoading ? 'not-allowed' : 'pointer', fontWeight: '600' }}>
                                            {shareLoading ? '생성 중...' : '+ 링크 생성'}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        </>
                    )
                }

                {/* ── 푸터 ── */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    padding: '12px 20px',
                    borderTop: '1px solid #e5e7eb',
                    background: '#fff',
                }}>
                    <button type="button" className="btn-cancel" onClick={onClose}>닫기</button>
                    {isOwner && status !== 'completed' && (
                        <button type="button" className="btn-save" onClick={() => { onReview(cardId); onClose(); }}>
                            검수하기
                        </button>
                    )}
                </div>
            </div>
        </ReactModal>
    );
}
